import Layout from '@components/layout/layout-six'
import Seo from '@components/seo/seo'
import Breadcrumb from '@components/ui/breadcrumb'
import Container from '@components/ui/container'
import * as magento from '@framework/magento'
import { Category } from '@framework/types'
import * as typesense from '@framework/typesense'
import { siteSettings } from '@settings/site-settings'
import { GetStaticProps } from 'next'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import Header from '@components/category/category-header'
import CategoryCardLanding from '@components/category/category-card-landing'
import BenefitsBar from '@components/common/benefits-bar'
import { Element } from 'react-scroll'
import RelatedProductFeed from '@components/product/feeds/related-product-feed'
import { getFeaturedCategoryProducts } from '@framework/typesense'

export default function ClassPage({
  catInfo,
  categories,
  featuredCategoryProducts,
  locale,
}: any) {
  const { t: translate } = useTranslation([
    'common',
    'categories',
    'home',
    'catBlurbs',
  ])

  return (
    <>
      {catInfo && (
        <Seo
          title={
            translate(catInfo?.name?.toLowerCase(), { ns: 'categories' }) ||
            catInfo?.name
          }
          description={`${translate('text-class-seo-blurb')} ${translate(
            catInfo?.name?.toLowerCase(),
            { ns: 'categories' },
          )?.toLocaleLowerCase()}.`}
          path={
            locale !== siteSettings.defaultLanguage
              ? `${locale}/${catInfo.url_path}`
              : catInfo?.url_path
          }
          canonical={
            locale !== siteSettings.defaultLanguage
              ? `${locale}/${catInfo.url_path}`
              : catInfo?.url_path
          }
        />
      )}
      <>
        <div className="w-full pt-4 pl-2">
          <Breadcrumb pathname={`/${catInfo?.url_path}`} />
        </div>
        <Header
          title={catInfo?.name}
          className="cat-level-1-hdr"
          image={
            `/assets/images/category_bg_foods.png` /* @TODO: this will be dynamic once Hamza produces the other header images... */
          }
        />
        <Container>
          {/* @ts-ignore */}
          <Element name="grid" className="flex pb-16 pt-1 sm:pt-7 lg:pb-20">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
              {categories?.map((category: any, index: any) => (
                <CategoryCardLanding
                  key={category.id}
                  image={category.icon}
                  title={category?.name}
                  description={translate(category?.name, {
                    ns: 'catBlurbs',
                  })}
                  slug={category.slug}
                  subcats={category.subcats}
                />
              ))}
            </div>
          </Element>
          <div
            className="mt-5  overflow-x-auto"
            id="you-might-also-like-section"
          >
            {locale && (
              <RelatedProductFeed
                className="mb-0.5 md:mb-2 lg:mb-3.5 xl:mb-4 2xl:mb-6"
                hits={featuredCategoryProducts}
                sectionHeading="text-bestsellers"
                titleClassName=""
              />
            )}
          </div>
        </Container>
        <BenefitsBar />
      </>
    </>
  )
}

ClassPage.Layout = Layout

export const getStaticProps: GetStaticProps = async (context: any) => {
  const { locale } = context
  const catInfo =
    (await magento.getCategoryByPath(`${context.params.class}.html`)) ||
    ({} as any)
  const ids: [] = catInfo.children.split(',')
  const categories: Category[] = []
  const featuredCategoryProducts = await getFeaturedCategoryProducts(locale, {
    categoryLevel: 2,
    categoryName: catInfo.name,
  })

  // Pull level 2 categories from typesense so that we only show cats with products
  const type_cats = await typesense.getCategories(2, locale, catInfo.name)
  for (let i = 0; i < ids.length; i++) {
    const child: any = ids[i]
    let subcat = (await magento.getCategoryById(Number(child))) || ({} as any)
    let subcats = await magento.getChildrenCategories(Number(child))
    if (
      type_cats?.[0].counts?.findIndex(
        (tc: any) => tc.value === subcat.name,
      ) !== -1
    ) {
      categories.push({
        id: subcat.id,
        name: subcat.name,
        slug: subcat.url_path,
        subcats: subcats,
        image: {
          id: `${subcat.id}-t`,
          thumbnail: subcat.preview_image,
          original: subcat.preview_image,
        },
        icon: subcat.preview_image,
        productCount: type_cats?.[0].counts?.find(
          (tc: any) => tc.value === subcat.name,
        )?.count,
      })
    }
  }

  return {
    props: {
      categories,
      catInfo,
      locale,
      featuredCategoryProducts,
      ...(await serverSideTranslations(context.locale!, [
        'categories',
        'common',
        'forms',
        'menu',
        'footer',
        'home',
        'catBlurbs',
      ])),
    },
    revalidate: 60,
  }
}

// This function gets called at build time to obtain the various paths that could call this page
export const getStaticPaths = async () => {
  const paths: any[] = []
  const classes: string[] = [
    'foods',
    'beverages',
    'international-foods',
    'disposables',
    'janitorial-supplies',
    'kitchen-smallware',
    'dinnerware',
    'kitchen-equipment',
    'health-and-personal-care',
  ]
  classes.map((c) =>
    paths.push({
      params: {
        class: c,
      },
    }),
  )

  return {
    paths,
    fallback: true, // false or 'blocking'
  }
}
